"use client";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useContractABI } from "@/hooks/useContractABI";
import { useChainInfo } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { FilterDisplay } from "../FilterDisplay";
interface EvmLogPayloadProps {
  chainId?: number;
  contractAddress?: string;
  eventAbi?: string;
  filter?: unknown;
}
interface EventInput {
  name: string;
  type: string;
  indexed?: boolean;
}

/**
 * EvmLogPayload - Specialized component for rendering EVM Log Trigger payloads
 * Displays contract address, chain info, event details, and filters
 */
export function EvmLogPayload({
  chainId,
  contractAddress,
  eventAbi,
  filter
}: EvmLogPayloadProps) {
  const {
    data: chainInfo
  } = useChainInfo(chainId);

  // Fetch contract ABI to get contract name/nameTag
  const {
    nameTag,
    contractName
  } = useContractABI({
    chainId,
    address: contractAddress,
    enabled: !!contractAddress && !!chainId
  });

  // Use nameTag first, fall back to contractName
  const displayName = nameTag || contractName;

  // Helper to format address as 0x12...abcdef (first 4 + last 6 chars)
  const formatAddress = (address: string) => {
    if (!address || address.length < 42) return address;
    return `${address.slice(0, 4)}...${address.slice(-6)}`;
  };

  // Parse event info from ABI
  let eventName = "";
  let eventInputs: EventInput[] = [];
  if (eventAbi) {
    try {
      const parsed = typeof eventAbi === "string" ? JSON.parse(eventAbi) : eventAbi;
      if (parsed.name) {
        eventName = parsed.name;
        eventInputs = parsed.inputs || [];
      } else if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].name) {
        eventName = parsed[0].name;
        eventInputs = parsed[0].inputs || [];
      }
    } catch {
      // Invalid JSON, ignore
    }
  }
  return <div className="border-ds-neutral-100 dark:border-ds-neutral-700 space-y-3 border-t pt-3">
      {/* Contract Address on Chain - Consolidated */}
      {contractAddress && chainInfo && <div className="space-y-1">
          <span className="table-header">Contract:</span>
          <div className="dark:bg-ds-neutral-900 flex items-center gap-2 rounded-lg bg-zinc-50 px-3 py-2">
            {chainInfo.explorers && chainInfo.explorers.length > 0 ? <a href={`${chainInfo.explorers[0].url}/address/${contractAddress}#code`} target="_blank" rel="noopener noreferrer" className="font-mono text-xs text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-400 dark:hover:text-blue-300" title={contractAddress}>
                {displayName ? <span>
                    <span className="font-semibold">{displayName}</span>
                    <span className="ml-1.5 text-blue-500 dark:text-blue-400">({formatAddress(contractAddress)})</span>
                  </span> : contractAddress}
              </a> : <span className="text-ds-text-secondary font-mono text-xs" title={contractAddress}>
                {displayName ? <span>
                    <span className="font-semibold">{displayName}</span>
                    <span className="text-ds-text-muted ml-1.5">({formatAddress(contractAddress)})</span>
                  </span> : contractAddress}
              </span>}
            <span className="meta-xs">on</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex cursor-help items-center gap-1.5">
                    {chainInfo.iconUrl && <img src={chainInfo.iconUrl} alt={chainInfo.name} className="h-4 w-4 rounded-full" />}
                    <span className="text-ds-text-primary text-xs font-semibold">{chainInfo.name}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Chain ID: {chainInfo.chainId}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>}

      {/* Event Name and Parameters */}
      {eventName ? <div className="space-y-2">
          <span className="table-header">Event:</span>
          <div className="rounded-md bg-green-50 px-3 py-2 dark:bg-green-500/10">
            <div className="font-mono text-xs font-semibold text-green-700 dark:text-green-400">{eventName}</div>
            {eventInputs.length > 0 && <div className="mt-2 space-y-1.5">
                <p className="text-xs font-medium text-green-700 dark:text-green-400">
                  Parameters ({eventInputs.length}):
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {eventInputs.map((input, idx) => <div key={idx} className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs dark:bg-green-900/30">
                      <span className="font-mono font-medium text-green-900 dark:text-green-100">{input.name}</span>
                      <span className="text-green-700 dark:text-green-300">{input.type}</span>
                      {input.indexed && <span className="text-3xs rounded-full bg-green-200 px-1.5 py-0.5 font-medium text-green-800 dark:bg-green-700 dark:text-green-200">
                          indexed
                        </span>}
                    </div>)}
                </div>
              </div>}
          </div>
        </div> : null}

      {/* Filter */}
      {filter ? <FilterDisplay filter={filter} size="sm" /> : null}
    </div>;
}